// src/components/layout/MenuPrincipal.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Box,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Button,
  Tooltip,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  BusinessOutlined,
  ReceiptOutlined,
  LinkOutlined,
  SettingsOutlined,
  VpnKeyOutlined,
  ExitToAppOutlined,
  HomeOutlined,
  LockOutlined,
  CloudUploadOutlined,
  ToggleOnOutlined,
  CloudDownloadOutlined,
  TableChartOutlined,
  AccountBalanceWalletOutlined,
  Sailing as SailingIcon,
  AssignmentTurnedIn,
  SummarizeOutlined,
  HistoryOutlined,
  ArticleOutlined,
  VerifiedOutlined,
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import { setEmpresaActiva, initEmpresaActiva } from '../../redux/empresaSlice';
import { useAuth } from '../../components/auth/AuthContext';
// Ya no necesitamos importar clearToken
// import { clearToken } from '../../redux/authSlice';

// Ancho del drawer del menú
const drawerWidth = 240;

const MenuPrincipal = ({ children }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [empresasMenuAnchor, setEmpresasMenuAnchor] = useState(null);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { isAuthenticated } = useSelector(state => state.auth);
  const { empresas, empresaActiva } = useSelector(state => state.empresa);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { logout, user } = useAuth();
  const esVisador = user?.permissions?.['rcv.visador'] || user?.is_superuser;
  const esResumen = user?.permissions?.['rcv.resumen'] || user?.is_superuser;
  const esBitacora = user?.permissions?.['rcv.bitacora'] || user?.is_superuser;
  const esSuperuser = user?.is_superuser;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Cargar la empresa activa desde localStorage al inicio
  useEffect(() => {
    dispatch(initEmpresaActiva());
  }, [dispatch]);
  
  // Funciones para manejar menús
  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };
  
  const handleEmpresasMenuOpen = (event) => {
    setEmpresasMenuAnchor(event.currentTarget);
  };
  
  const handleEmpresasMenuClose = () => {
    setEmpresasMenuAnchor(null);
  };
  
  // Eliminamos la función handleLogout ya que no se necesita
  
  const handleSelectEmpresa = (empresa) => {
    dispatch(setEmpresaActiva(empresa));
    handleEmpresasMenuClose();
    // Si estamos en la página de certificados, navegar al RUT de la nueva empresa
    if (window.location.pathname.startsWith('/certificados')) {
      navigate(`/certificados/${empresa.rut}`);
    }
  };
  
  const handleNavigate = (route) => {
    navigate(route);
    setMenuOpen(false);
  };
  
  return (
    <Box sx={{ display: 'flex', overflow: 'hidden', width: '100%' }}>
      {/* AppBar (barra superior) */}
      <AppBar position="fixed" sx={{ zIndex: theme => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="abrir menú"
            edge="start"
            onClick={() => setMenuOpen(!menuOpen)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, minWidth: 0 }}>
            Sistema RCV SII
          </Typography>
          
          {/* Selector de empresa */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            {/* Desktop: full text button */}
            <Button
              color="inherit"
              onClick={handleEmpresasMenuOpen}
              endIcon={<BusinessOutlined />}
              sx={{ textTransform: 'none', display: { xs: 'none', sm: 'flex' } }}
            >
              {empresaActiva ? `${empresaActiva.rut}-${empresaActiva.dv}: ${empresaActiva.nombre_corto || empresaActiva.razon_social}` : 'Seleccionar empresa'}
            </Button>
            {/* Mobile: icon-only button */}
            <IconButton
              color="inherit"
              onClick={handleEmpresasMenuOpen}
              sx={{ display: { xs: 'flex', sm: 'none' } }}
            >
              <BusinessOutlined />
            </IconButton>

            <Menu
              anchorEl={empresasMenuAnchor}
              open={Boolean(empresasMenuAnchor)}
              onClose={handleEmpresasMenuClose}
              PaperProps={{
                style: {
                  maxHeight: 48 * 5.5,
                  width: '250px',
                },
              }}
            >
              <MenuItem sx={{ justifyContent: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Seleccionar organización
                </Typography>
              </MenuItem>
              <Divider />

              {empresas.map((empresa) => (
                <MenuItem
                  key={empresa.id}
                  onClick={() => handleSelectEmpresa(empresa)}
                  selected={empresaActiva && empresaActiva.id === empresa.id}
                >
                  <ListItemIcon>
                    <BusinessOutlined color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${empresa.rut}-${empresa.dv}`}
                    secondary={empresa.nombre_corto || empresa.razon_social}
                  />
                </MenuItem>
              ))}

              <Divider />
              <MenuItem onClick={() => { handleEmpresasMenuClose(); handleNavigate('/empresas'); }}>
                <ListItemIcon>
                  <SettingsOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Organizaciones" />
              </MenuItem>
            </Menu>
          </Box>
          
          {/* Estado de conexión con SII */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, mr: 2 }}>
            {isAuthenticated ? (
              <Tooltip title="TokenSII activo (válido por ~1 hora)">
                <Chip
                  icon={<ToggleOnOutlined />}
                  label="TokenSII activo"
                  color="success"
                  size="small"
                  variant="outlined"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
                  onClick={() => navigate('/autenticacion')}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Se requiere TokenSII para consultar datos">
                <Chip
                  label="TokenSII requerido"
                  color="default"
                  size="small"
                  variant="outlined"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
                  onClick={() => navigate('/autenticacion')}
                />
              </Tooltip>
            )}
          </Box>
          
          {/* Menú de herramientas */}
          <Tooltip title="Herramientas">
            <IconButton 
              onClick={handleUserMenuOpen} 
              color="inherit"
              sx={{ ml: 1 }}
            >
              <SettingsOutlined />
            </IconButton>
          </Tooltip>
          
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => { handleUserMenuClose(); handleNavigate('/autenticacion'); }}>
              <ListItemIcon>
                <VpnKeyOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Obtener Token SII" />
            </MenuItem>
            
            <MenuItem onClick={() => { handleUserMenuClose(); handleNavigate('/empresas'); }}>
              <ListItemIcon>
                <BusinessOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Organizaciones" />
            </MenuItem>
            
            <MenuItem onClick={() => { handleUserMenuClose(); handleNavigate('/certificados'); }}>
              <ListItemIcon>
                <VpnKeyOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Certificados" />
            </MenuItem>
            
            <Divider />
            
            <MenuItem onClick={() => { handleUserMenuClose(); handleLogout(); }}>
              <ListItemIcon>
                <ExitToAppOutlined fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Cerrar Sesión" />
            </MenuItem>
            
          </Menu>
        </Toolbar>
      </AppBar>
      
      {/* Menú lateral */}
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', mt: 2 }}>
          <List>
            <ListItemButton onClick={() => handleNavigate('/')}>
              <ListItemIcon>
                <HomeOutlined />
              </ListItemIcon>
              <ListItemText primary="Inicio" />
            </ListItemButton>
            
            <Divider sx={{ my: 1 }} />
            
            <ListItemButton onClick={() => handleNavigate('/autenticacion')}>
              <ListItemIcon>
                <LockOutlined />
              </ListItemIcon>
              <ListItemText primary="Obtener Token SII" />
            </ListItemButton>
            
            <ListItemButton 
              onClick={() => handleNavigate('/rcv/consulta')}
              disabled={!isAuthenticated || !empresaActiva}
            >
              <ListItemIcon>
                <ReceiptOutlined />
              </ListItemIcon>
              <ListItemText primary="Consulta RCV" />
            </ListItemButton>
            
            <ListItemButton 
              onClick={() => handleNavigate('/rcv/csv-upload')}
              disabled={!empresaActiva}
            >
              <ListItemIcon>
                <CloudUploadOutlined />
              </ListItemIcon>
              <ListItemText primary="Cargar CSV RCV" />
            </ListItemButton>

            <ListItemButton 
              onClick={() => handleNavigate('/rcv/documentos')}
              disabled={!empresaActiva}
            >
              <ListItemIcon>
                <AccountBalanceWalletOutlined />
              </ListItemIcon>
              <ListItemText primary="Documentos" />
            </ListItemButton>
            
            <ListItemButton
              onClick={() => handleNavigate('/rcv/asociaciones')}
              disabled={!empresaActiva}
            >
              <ListItemIcon>
                <LinkOutlined />
              </ListItemIcon>
              <ListItemText primary="Asociaciones" />
            </ListItemButton>

            {esResumen && (
              <ListItemButton
                onClick={() => handleNavigate('/rcv/resumen')}
                disabled={!empresaActiva}
              >
                <ListItemIcon>
                  <SummarizeOutlined />
                </ListItemIcon>
                <ListItemText primary="Resumen C&V" />
              </ListItemButton>
            )}

            {esVisador && (
              <ListItemButton onClick={() => handleNavigate('/visar')}>
                <ListItemIcon>
                  <AssignmentTurnedIn />
                </ListItemIcon>
                <ListItemText primary="Visar documentos" />
              </ListItemButton>
            )}

            {esBitacora && (
              <ListItemButton onClick={() => handleNavigate('/bitacora')}>
                <ListItemIcon>
                  <HistoryOutlined />
                </ListItemIcon>
                <ListItemText primary="Bitácora" />
              </ListItemButton>
            )}

            {/* Exportación Contable - Deshabilitado */}
            {/* <ListItemButton
              onClick={() => handleNavigate('/rcv/exportacion')}
              disabled={!empresaActiva}
            >
              <ListItemIcon>
                <CloudDownloadOutlined />
              </ListItemIcon>
              <ListItemText primary="Exportación Contable" />
            </ListItemButton> */}


            <ListItemButton
              onClick={() => handleNavigate('/tesoreria')}
              disabled={!empresaActiva}
            >
              <ListItemIcon>
                <TableChartOutlined />
              </ListItemIcon>
              <ListItemText primary="Tesorería" />
            </ListItemButton>

            <ListItemButton
              onClick={() => handleNavigate('/pesca')}
              disabled={!empresaActiva}
            >
              <ListItemIcon>
                <SailingIcon />
              </ListItemIcon>
              <ListItemText primary="Pesca" />
            </ListItemButton>

            <Divider sx={{ my: 1 }} />

            <ListItemButton onClick={() => handleNavigate('/empresas')}>
              <ListItemIcon>
                <BusinessOutlined />
              </ListItemIcon>
              <ListItemText primary="Organizaciones" />
            </ListItemButton>
            
            <ListItemButton onClick={() => handleNavigate('/certificados')}>
              <ListItemIcon>
                <VpnKeyOutlined />
              </ListItemIcon>
              <ListItemText primary="Certificados" />
            </ListItemButton>
            
            {esSuperuser && (
              <>
                <Divider sx={{ my: 1 }} />
                <ListItemButton onClick={() => handleNavigate('/emision')}>
                  <ListItemIcon>
                    <ArticleOutlined />
                  </ListItemIcon>
                  <ListItemText
                    primary="Emisión DTE"
                    secondary="Documentos tributarios electrónicos"
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
                <ListItemButton onClick={() => handleNavigate('/certificacion')}>
                  <ListItemIcon>
                    <VerifiedOutlined />
                  </ListItemIcon>
                  <ListItemText
                    primary="Certificación DTE"
                    secondary="Proceso de certificación SII"
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              </>
            )}

            <Divider sx={{ my: 1 }} />

            <ListItemButton onClick={() => handleNavigate('/configuracion')}>
              <ListItemIcon>
                <SettingsOutlined />
              </ListItemIcon>
              <ListItemText primary="Configuración" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
      
      {/* Contenido principal */}
      <Box component="main" sx={{
        flexGrow: 1,
        minWidth: 0,
        overflow: 'hidden',
        p: { xs: 1.5, sm: 3 },
        width: { xs: '100%', sm: menuOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
        marginLeft: { sm: menuOpen ? `${drawerWidth}px` : 0 },
        transition: theme => theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.sharp,
          duration: menuOpen ?
            theme.transitions.duration.enteringScreen :
            theme.transitions.duration.leavingScreen,
        }),
      }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default MenuPrincipal;